"use client";
import { type FC, type SubmitEventHandler, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTransitionRouter } from "next-view-transitions";
import { track } from "@vercel/analytics";
import posthog from "posthog-js";

import type { HttpError } from "@vita-learning/types";

import {
	RegisterForm,
	type RegisterFormOnSubmitProps,
} from "~/client/components/forms/RegisterForm";
import { usePopupQueue } from "~/client/components/portals/Popup";
import {
	createUser,
	makeHandleHttpError,
	makePosthogEventName as ph,
	userToPostHogUser,
} from "~/shared/services";
import { makeUrl } from "~/shared/utils/makeUrl";

import { useImportPreviewResource } from "../useImportPreviewResource";

export const Register: FC = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<HttpError | null>(null);
	const router = useTransitionRouter();
	const { enqueueError } = usePopupQueue();
	const searchParams = useSearchParams();

	const importPreviewResource = useImportPreviewResource();

	const makeOnSubmit = ({ email, isEnabled }: RegisterFormOnSubmitProps) => {
		const handleSubmit: SubmitEventHandler<HTMLFormElement> = async e => {
			e.preventDefault();

			try {
				if (!isEnabled) return;

				setIsLoading(true);

				const user = await createUser({
					email,
					utm_source: searchParams?.get("utm_source") ?? undefined,
					utm_medium: searchParams?.get("utm_medium") ?? undefined,
					utm_campaign: searchParams?.get("utm_campaign") ?? undefined,
				});

				await importPreviewResource(user);

				const url = makeUrl("/welcome");

				url.searchParams.set("email", user.email);

				posthog.identify(user.id, userToPostHogUser(user));
				posthog.capture(ph("signup_flow", "user", "created"), {
					utm_source: searchParams?.get("utm_source"),
					utm_medium: searchParams?.get("utm_medium"),
					utm_campaign: searchParams?.get("utm_campaign"),
				});
				track("Create Account", { email: user.email });

				router.push(url.pathname + url.search);
			} catch (e) {
				const handleHttpError = makeHandleHttpError("REGISTER_ERROR");

				const error = handleHttpError(e);

				setError(error);

				if (error.id !== "USER_EMAIL") enqueueError(error.message);
			} finally {
				setTimeout(() => setIsLoading(false), 500);
			}
		};

		return handleSubmit;
	};

	useEffect(() => {
		const _id = searchParams?.get("_id");

		if (!_id) return;

		posthog.identify(_id);
	}, [searchParams]);

	return (
		<RegisterForm
			className="auth__form"
			makeOnSubmit={makeOnSubmit}
			error={error}
			isLoading={isLoading}
		/>
	);
};
